import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, CheckCircle2, Calendar, Clock, User, Tag, ExternalLink,
  FileText, List, CalendarDays, Save, BadgeDollarSign, Sparkles,
  XCircle, RotateCcw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import WeekCalendar from "../components/WeekCalendar";

const STATUS_LABELS = {
  pendiente: "Pendiente",
  impartida: "Completada",
  no_show_student: "No asistió alumno",
  no_show_teacher: "No asistió profesora",
  reprogramada: "Reprogramada",
};

const formatStamp = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TeacherDashboard = () => {
  const { user, logout } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);
  const [view, setView] = useState("list");
  const [notesDraft, setNotesDraft] = useState({});
  const [savingNotes, setSavingNotes] = useState(null);
  const [savedFlash, setSavedFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/teacher/classes");
      setClasses(data);
      const drafts = {};
      data.forEach((c) => { drafts[c.id] = c.teacher_notes || ""; });
      setNotesDraft(drafts);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, newStatus) => {
    setUpdating(id);
    try {
      const { data } = await api.post(`/teacher/classes/${id}/status`, {
        status: newStatus,
        teacher_notes: notesDraft[id] ?? "",
      });
      setClasses((prev) => prev.map((c) => (c.id === id ? data : c)));
      setSavedFlash(id);
      setTimeout(() => setSavedFlash(null), 1800);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUpdating(null);
    }
  };

  const saveNotes = async (id) => {
    setSavingNotes(id);
    try {
      const { data } = await api.put(`/teacher/classes/${id}/notes`, {
        teacher_notes: notesDraft[id] ?? "",
      });
      setClasses((prev) => prev.map((c) => (c.id === id ? data : c)));
      setSavedFlash(id);
      setTimeout(() => setSavedFlash(null), 1800);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingNotes(null);
    }
  };

  const pending = classes.filter((c) => c.status === "pendiente");
  const completed = classes.filter((c) => c.status === "impartida");

  return (
    <div className="dash-shell" data-testid="teacher-dashboard">
      <header className="dash-header">
        <div className="dash-header-inner">
          <Link to="/" className="dash-brand" data-testid="dash-brand">
            <span className="dash-brand-text">Laboratorio Secreto</span>
            <span className="dash-brand-sub">Panel de profesoras</span>
          </Link>
          <div className="dash-user">
            <div className="dash-user-meta">
              <span className="dash-user-name">{user?.name}</span>
              <span className="dash-user-email">{user?.email}</span>
            </div>
            <button onClick={logout} className="dash-logout" data-testid="logout-button">
              <LogOut size={16} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main className="dash-main">
        <div className="dash-container">
          <div className="dash-summary">
            <div className="dash-stat">
              <span className="dash-stat-value">{pending.length}</span>
              <span className="dash-stat-label">Pendientes</span>
            </div>
            <div className="dash-stat dash-stat-success">
              <span className="dash-stat-value">{completed.length}</span>
              <span className="dash-stat-label">Completadas</span>
            </div>
            <div className="dash-stat dash-stat-muted">
              <span className="dash-stat-value">{classes.length}</span>
              <span className="dash-stat-label">Total asignadas</span>
            </div>
          </div>

          <div className="view-toolbar">
            <h2 className="dash-section-title">Mis clases</h2>
            <div className="view-switch" role="tablist" aria-label="Vista">
              <button
                className={`view-btn ${view === "list" ? "is-active" : ""}`}
                onClick={() => setView("list")}
                data-testid="view-list"
              >
                <List size={14} /> Lista
              </button>
              <button
                className={`view-btn ${view === "calendar" ? "is-active" : ""}`}
                onClick={() => setView("calendar")}
                data-testid="view-calendar"
              >
                <CalendarDays size={14} /> Calendario
              </button>
            </div>
          </div>

          {loading && <div className="dash-empty">Cargando clases…</div>}
          {error && !loading && <div className="dash-empty dash-error">{error}</div>}
          {!loading && !error && classes.length === 0 && (
            <div className="dash-empty">No tienes clases asignadas todavía.</div>
          )}

          {!loading && !error && classes.length > 0 && view === "calendar" && (
            <WeekCalendar
              classes={classes}
              onClassClick={(c) => {
                setView("list");
                setTimeout(() => {
                  const el = document.querySelector(`[data-testid="class-card-${c.id}"]`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 50);
              }}
            />
          )}

          {!loading && !error && classes.length > 0 && view === "list" && (
            <div className="dash-list" data-testid="teacher-classes-list">
              {classes.map((c) => {
                const isFinal = c.status !== "pendiente";
                return (
                  <article
                    key={c.id}
                    className={`dash-class status-${c.status} ${isFinal ? "is-done" : ""}`}
                    data-testid={`class-card-${c.id}`}
                  >
                    <div className="dash-class-main">
                      <div className="dash-class-meta">
                        <span className="dash-meta">
                          <Calendar size={14} /> {c.date}
                        </span>
                        <span className="dash-meta">
                          <Clock size={14} /> {c.time}
                        </span>
                        <span className="dash-meta">
                          <Tag size={14} /> {c.class_type}
                        </span>
                        <span
                          className={`dash-cat dash-cat-${c.category || "paid"}`}
                          data-testid={`class-category-${c.id}`}
                        >
                          {c.category === "trial" ? (
                            <><Sparkles size={12} /> Prueba</>
                          ) : (
                            <><BadgeDollarSign size={12} /> Clase pagada</>
                          )}
                        </span>
                      </div>
                      <h3 className="dash-class-student">
                        <User size={16} /> {c.student_name}
                      </h3>
                      {c.link && (
                        <a
                          href={c.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dash-class-link"
                          data-testid={`class-link-${c.id}`}
                        >
                          <ExternalLink size={14} /> Link de la clase
                        </a>
                      )}
                      {c.notas && (
                        <p className="dash-class-notes">
                          <FileText size={13} /> <strong>Notas internas:</strong> {c.notas}
                        </p>
                      )}

                      <div className="teacher-notes-block">
                        <label
                          htmlFor={`tnotes-${c.id}`}
                          className="teacher-notes-label"
                        >
                          <FileText size={13} /> Comentarios de la profesora
                        </label>
                        <textarea
                          id={`tnotes-${c.id}`}
                          className="teacher-notes-input"
                          rows="2"
                          placeholder="Escribe aquí tus observaciones de la clase…"
                          value={notesDraft[c.id] ?? ""}
                          onChange={(e) =>
                            setNotesDraft((d) => ({ ...d, [c.id]: e.target.value }))
                          }
                          data-testid={`teacher-notes-input-${c.id}`}
                        />
                        <div className="teacher-notes-actions">
                          <button
                            type="button"
                            className="dash-mark-btn dash-mark-btn-ghost"
                            onClick={() => saveNotes(c.id)}
                            disabled={savingNotes === c.id}
                            data-testid={`save-notes-${c.id}`}
                          >
                            <Save size={14} />
                            <span>{savingNotes === c.id ? "Guardando…" : "Guardar comentarios"}</span>
                          </button>
                          {savedFlash === c.id && (
                            <span className="notes-saved-flash" data-testid={`notes-saved-${c.id}`}>
                              <CheckCircle2 size={14} /> Guardado
                            </span>
                          )}
                        </div>
                        {c.notes_updated_at && (
                          <div className="audit-stamp" data-testid={`notes-stamp-${c.id}`}>
                            Última actualización de comentarios: {formatStamp(c.notes_updated_at)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="dash-class-side">
                      <span
                        className={`dash-status dash-status-${c.status}`}
                        data-testid={`class-status-${c.id}`}
                      >
                        {STATUS_LABELS[c.status] || c.status}
                      </span>

                      <div className="status-actions" data-testid={`status-actions-${c.id}`}>
                        <button
                          onClick={() => setStatus(c.id, "impartida")}
                          disabled={updating === c.id || c.status === "impartida"}
                          className="status-btn status-btn-success"
                          data-testid={`mark-completed-${c.id}`}
                        >
                          <CheckCircle2 size={14} />
                          <span>Completada</span>
                        </button>
                        <button
                          onClick={() => setStatus(c.id, "no_show_student")}
                          disabled={updating === c.id || c.status === "no_show_student"}
                          className="status-btn status-btn-warn"
                          data-testid={`mark-noshow-${c.id}`}
                        >
                          <XCircle size={14} />
                          <span>No asistió</span>
                        </button>
                        <button
                          onClick={() => setStatus(c.id, "reprogramada")}
                          disabled={updating === c.id || c.status === "reprogramada"}
                          className="status-btn status-btn-info"
                          data-testid={`mark-rescheduled-${c.id}`}
                        >
                          <RotateCcw size={14} />
                          <span>Reprogramada</span>
                        </button>
                      </div>

                      {c.status_set_at && (
                        <div className="audit-stamp" data-testid={`status-stamp-${c.id}`}>
                          Marcada: {formatStamp(c.status_set_at)}
                        </div>
                      )}

                      {/* Backward-compat alias for legacy testing selectors */}
                      <button
                        onClick={() => setStatus(c.id, "impartida")}
                        disabled={updating === c.id || c.status === "impartida"}
                        className="dash-mark-btn sr-only-btn"
                        data-testid={`mark-impartida-${c.id}`}
                        aria-hidden="true"
                        tabIndex={-1}
                      >
                        Marcar como impartida
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeacherDashboard;
